// Otto's view of the ASSET filter catalog — the adapter between this page's filter panel and the contract
// Otto's filter capability speaks. Two directions, both pure and both mirrors of code that lives elsewhere
// in this module (registered with otto-sync for exactly that reason):
//
//   buildOttoAssetFilterFields  — components/FilterModal/constants.js `filterFields`  ->  Otto's field shape
//   buildAssetFilterParams      — Otto's resolved apply payload                       ->  submitMasterFilter's
//
// The two catalogs are close but not identical, and the three differences are the whole reason this file
// exists:
//
//   1. `getListKey` is a STRING here ("asset_types.name") and an ARRAY on the dashboard
//      (["clients.id","clients.name"]). Otto derives `value_kind` from the array's LENGTH — two columns
//      means the option carries an id, one means the option IS its value. Passing the raw string through
//      would make `.length` the character count, so every asset field would look like an id dimension and
//      Otto would apply option IDS to a panel that stores NAMES.
//   2. Visibility is not a `hide` flag but a per-user config (`assetViewFieldConfig`). Mapping it ONTO
//      `hide` is what makes the FTO-70 gate apply here for free: buildAvailableFields drops hidden fields
//      from everything Otto offers, and the apply layer refuses them as defence in depth.
//   3. Applied values are option NAMES under the field's `paramKey`, and wildcards live under a separate
//      `filters` object keyed by `searchKey`. The dashboard's mapper applies IDS and its own param shape,
//      so it cannot be reused.
import { filterFields as ASSET_FILTER_FIELDS } from "./components/FilterModal/constants";
import { isAssetFilterFieldAdminHidden } from "./fieldVisibility/assetViewFieldConfigUtils";
import { toPanelWildcardEntry } from "../filterAssistant/core/wildcardOperators";

// Fields whose value is a boolean-ish scalar the page lifts OUT of `list` into its own param
// (submitMasterFilter reads has_image / verified_survey off the top level). Otto does not offer them as
// dimensions — their handling is bespoke and getting it wrong applies a filter nobody asked for.
const EXCLUDED_KEYS = new Set(["has_image", "verified_survey"]);

// ------------------------------------------------------------------------------------------------------
// THE TWO SHAPES. Reading and writing this panel's filters are NOT the same object, and conflating them is
// what broke FTO-78.
//
//   READ  (getCurrentFilters, both mounts):  { filters, list: {…selections…}, client_id?, region_id?, … }
//   WRITE (submitMasterFilter, both mounts): { filters, …selections }        <- selections spread FLAT
//
// `submitMasterFilter` makes the new `list` out of EVERY key except `filters` (`const { filters, ...rest }
// = filterData; list: rest`), and useMyFilters seeds its working copy the mirror way
// (`{ filters, ...currentFilters.list }`). So a write payload must carry the selections FLAT and nothing
// else — the scope keys come from the route, not the panel.
//
// Spreading the READ shape into a write sent `list` (an object) and `client_id` (a string) back as if they
// were selections, and submitMasterFilter re-wrapped the lot:
//     chartParams.list = { list: {…the real selections…}, client_id: "06356c0e-…", regions_name: ["Northeast"] }
// Three symptoms, one cause: the dashboard filtered on keys the API doesn't know (no data at all), the
// Region dropdown's cascade broke ("no Data"), and AppliedFilters crashed — `renderListFilter` takes the
// array branch on anything with a `.length`, a 36-char uuid has one, and `"06356c0e-…".map` is not a
// function. Same defect as the option-cascade nesting already fixed in ottoAssetOptions.js (commit
// ccea267ce) — the SAME `list` key, the other direction: corrected for the READ, missed on apply and undo.
//
// One converter, used by the apply payload AND by each mount's undo snapshot, so the shape can only be
// wrong in one place.
export const toAssetSubmitShape = (current = {}) => ({
    ...((current && current.list) || {}),
    // `me` ("assigned to me", Mobile Asset source only -- upstream FCA-7406) is a TOP-LEVEL sibling of
    // `list`/`filters` in BOTH shapes, never a member of `list`: getCurrentFilters returns it beside them
    // and submitMasterFilter destructures it back out (`const { filters, me = null, ...rest }`) before
    // folding everything else into `list`. Spreading `current.list` alone therefore dropped it, and since
    // the write defaults it to null, EVERY Otto apply on that source silently cleared a filter the user
    // had set -- the panel's own applied-count included. Carried only when actually set: the standalone
    // Assets page has no `me` and folds every non-`filters` key into `list`, so an unconditional key would
    // leak INTO `list` there, which is the client_id leak of FTO-78 pointing the other way.
    ...(current && current.me ? { me: current.me } : {}),
    filters: { ...((current && current.filters) || {}) }
});

/**
 * The asset filter catalog in Otto's `filterFields` shape.
 *
 * @param assetViewFieldConfig the page's resolved per-user field config (state.assetViewFieldConfig)
 * @returns fields consumable by buildAvailableFields / augmentMapsWithFilterFields
 */
export const buildOttoAssetFilterFields = ({ assetViewFieldConfig } = {}) =>
    (ASSET_FILTER_FIELDS || [])
        .filter(f => f && f.key && !EXCLUDED_KEYS.has(f.key))
        .map(f => ({
            key: f.key,
            label: f.label,
            paramKey: f.paramKey,
            band: f.band,
            searchKey: f.searchKey,
            isWildCardFilter: !!f.isWildCardFilter,
            fieldType: f.fieldType,
            // Single-element array => value_kind "value" => Otto applies the option NAME, which is exactly
            // what this page's filterData stores (useMyFilters.handleFilterUpdate matches on `opt.name`).
            ...(f.getListKey ? { getListKey: [f.getListKey] } : {}),
            // The raw string the /assets/get_list endpoint wants as its `field` param — kept verbatim so the
            // option fetch mirrors the panel's own call rather than reconstructing it.
            assetGetListKey: f.getListKey,
            // Routes candidate lookup to the asset endpoint instead of the recommendations catalog view,
            // which does not carry asset vocabulary (see ottoAssetOptions.js).
            optionSource: "assets",
            // The per-user gate, expressed as the flag Otto already enforces everywhere (FTO-70).
            hide: assetViewFieldConfig ? !!isAssetFilterFieldAdminHidden(f, assetViewFieldConfig) : false
        }));

// Index Otto's field list by the JSON of its getListKey — the join the apply payload uses (dimensions carry
// `get_list_field`, not the field key).
const indexByGetListKey = fields => {
    const index = {};
    (fields || []).forEach(f => {
        if (f && f.getListKey) index[JSON.stringify(f.getListKey)] = f;
    });
    return index;
};

const indexByKey = fields => {
    const index = {};
    (fields || []).forEach(f => {
        if (f && f.key) index[f.key] = f;
    });
    return index;
};

/**
 * Turn Otto's resolved apply payload into the object `submitMasterFilter` expects:
 *
 *   { filters: { [searchKey]: { key, filters: [operator] } },   // wildcards, operator TRANSLATED
 *     [paramKey]: [optionName, ...] }                           // dimensions, spread at the top level
 *
 * The wildcard operator is the panel's token ("less_than"), never the assistant's ("lt") — see
 * filterAssistant/core/wildcardOperators.js for why those are two different vocabularies.
 *
 * `current` is the page's current filter state (getCurrentFilters), so a new pick REFINES rather than
 * replaces — the same multi-turn behavior the dashboard and Recommendations have.
 *
 * Returns { params, clearedAll, diagnostics }. `diagnostics.dropped` is non-empty when a resolved value had
 * no field config to apply under: that is the silent-apply failure mode (Otto says "Applied" and the grid
 * never changes), so the caller marks the trace span as an error rather than letting it pass quietly.
 */
export const buildAssetFilterParams = ({ payload, filterFields = [], current = {} } = {}) => {
    const byGlf = indexByGetListKey(filterFields);
    const byKey = indexByKey(filterFields);
    const diagnostics = { applied: [], dropped: [], wildcardsApplied: [], wildcardsDropped: [], clears: [] };

    if (payload && payload.clear_all) {
        // Empty selections + empty wildcards, which is exactly what the panel's own Reset submits. The scope
        // keys are NOT part of this payload — they are re-derived from the route by getCurrentFilters, so
        // echoing them back here is what put `client_id` inside `list` (see THE TWO SHAPES above).
        return { params: { filters: {} }, clearedAll: true, diagnostics: { ...diagnostics, clearAll: true } };
    }

    // Selections come from `current.list`, never the top level. has_image / verified_survey live in there
    // too; Otto doesn't offer them (EXCLUDED_KEYS) but they ride along untouched, so an Otto apply can't
    // silently drop a filter the user set in the panel.
    const { filters: currentWildcards, ...currentDims } = toAssetSubmitShape(current);
    const params = { ...currentDims };
    const wildcards = { ...(currentWildcards || {}) };

    (((payload && payload.dimensions) || [])).forEach(d => {
        const cfg = byGlf[JSON.stringify(d.get_list_field)];
        if (!cfg || !cfg.paramKey) {
            diagnostics.dropped.push({ get_list_field: d.get_list_field, name: d.name, reason: cfg ? "no paramKey" : "no field config" });
            return;
        }
        // NAMES, not ids: this panel stores what the option is called (see the header note).
        const value = d.name != null ? d.name : d.id;
        if (value == null) {
            diagnostics.dropped.push({ get_list_field: d.get_list_field, reason: "no value" });
            return;
        }
        const existing = Array.isArray(params[cfg.paramKey]) ? params[cfg.paramKey] : [];
        // `set` replaces the field's selection, anything else appends — matching the dashboard's semantics
        // so a refinement ("also HVAC") accumulates while a fresh directive starts clean.
        const next = d.action === "set" ? [value] : existing.includes(value) ? existing : [...existing, value];
        params[cfg.paramKey] = next;
        diagnostics.applied.push({ paramKey: cfg.paramKey, value });
    });

    (((payload && payload.clears) || [])).forEach(c => {
        const cfg = byGlf[JSON.stringify(c.get_list_field)];
        if (!cfg || !cfg.paramKey) return;
        delete params[cfg.paramKey];
        diagnostics.clears.push(cfg.paramKey);
    });

    (((payload && payload.wildcards) || [])).forEach(w => {
        const cfg = byKey[w.field_id];
        const searchKey = cfg && (cfg.searchKey || cfg.paramKey);
        if (!searchKey) {
            diagnostics.wildcardsDropped.push({ field_id: w.field_id, reason: "no field config" });
            return;
        }
        // The panel's own wildcard shape (useMyFilters keeps an entry only when it has a text `key` or is
        // an emptiness operator), so an Otto-applied threshold is indistinguishable from a typed one — but
        // ONLY once the operator is translated. This line used to write `filters: [w.operator]`, i.e. the
        // interpret id ("lt"), which is in no panel dropdown and matches no Ruby scope: the user saw a value
        // with no operator and the filter did nothing (FTO-91). The shared translator is the same one the
        // landing dashboard uses; the two panels store the identical entry shape.
        const { entry, operator, dropped } = toPanelWildcardEntry({
            operator: w.operator,
            value: w.value,
            valueRange: w.value_range,
            fieldType: cfg.fieldType
        });
        if (dropped) {
            diagnostics.wildcardsDropped.push({ field_id: w.field_id, operator: w.operator, reason: dropped });
            return;
        }
        wildcards[searchKey] = entry;
        diagnostics.wildcardsApplied.push({ searchKey, operator });
    });

    (((payload && payload.clear_wildcards) || [])).forEach(fieldId => {
        const cfg = byKey[fieldId];
        const searchKey = cfg && (cfg.searchKey || cfg.paramKey);
        if (searchKey) delete wildcards[searchKey];
    });

    return { params: { ...params, filters: wildcards }, clearedAll: false, diagnostics };
};

/** The page's current filter state in Otto's activeFilters shape (what "which filters are active?" reads). */
export const buildAssetActiveFilters = (current = {}, filterFields = []) => {
    const byParamKey = {};
    (filterFields || []).forEach(f => {
        if (f && f.paramKey) byParamKey[f.paramKey] = f;
    });
    const dimensions = [];
    // Same READ shape as everywhere else: selections are under `list`. Reading the top level found only
    // `filters` (an object), `list` (an object) and the scope keys (strings) — none of them arrays — so
    // "which filters are active?" answered "none" on both asset pages no matter what was applied.
    Object.entries((current && current.list) || {}).forEach(([paramKey, value]) => {
        const cfg = byParamKey[paramKey];
        if (!cfg || !Array.isArray(value) || !value.length) return;
        dimensions.push({ key: cfg.key, label: cfg.label, get_list_field: cfg.getListKey, values: value.map(v => ({ name: v })) });
    });
    const wildcards = [];
    Object.entries((current && current.filters) || {}).forEach(([searchKey, entry]) => {
        const cfg = Object.values(byParamKey).find(f => f.searchKey === searchKey);
        if (!entry) return;
        wildcards.push({
            key: (cfg && cfg.key) || searchKey,
            label: (cfg && cfg.label) || searchKey,
            operator: (entry.filters || [])[0],
            value: entry.key
        });
    });
    return { dimensions, wildcards };
};
